import { StateStoreService } from '../services/state-store/state-store';
import type { AssessmentRecord } from '../models/document.model';

/**
 * Single source of truth for "can this viewer export this assessment?".
 *
 * Two gates, evaluated against the loaded assessment:
 *   1. The viewer must be able to act on the assessment's owning org.
 *      Standard users and managers only ever see assessments from orgs
 *      they're a member of, so the membership check is a no-op for them.
 *      Super-managers can REVIEW assessments owned by external orgs (via
 *      the review queue), but the BE refuses to serve the document — so
 *      we mirror the rule on the FE and disable the action with a
 *      tooltip explaining why, instead of letting the click 403.
 *   2. Standard users (no manager / super-manager role) additionally need
 *      an active subscription on their own org. Managers and super-
 *      managers bypass the subscription gate because their platform
 *      relationship is the entitlement.
 *
 * Both surfaces (list rows and detail drawers) feed the same `(state,
 * assessment)` pair through this util, so the disabled state on the
 * button and the tooltip stay in lockstep with the actual BE acceptance.
 *
 * Used by every surface that exposes an Export affordance: the
 * edit-assessment toolbar, the assessment-info-panel drawer menu, the
 * assessment-list card menu, and the assessments-for-review list menu.
 */
type ExportPermissionReason =
  | 'export_permissions.external_org'
  | 'export_permissions.requires_subscription'
  | 'export_permissions.no_document';

interface ExportPermission {
  /** True iff the viewer is allowed to trigger the export. */
  allowed: boolean;
  /** Translation key describing *why* not, when `allowed` is false.
   *  Surface this as a tooltip on the disabled button so the user
   *  understands the gate without having to click and read a toast. */
  reasonKey?: ExportPermissionReason;
}

export function getAssessmentExportPermission(
  state: StateStoreService,
  assessment: AssessmentRecord | null | undefined
): ExportPermission {
  if (!assessment) return { allowed: false, reasonKey: 'export_permissions.no_document' };

  // Super-managers can act on assessments owned by orgs they're a member
  // of. The review queue may surface assessments from external orgs for
  // verification — those can be read in-app, but the document store
  // declines downstream serving for non-member orgs. Match the BE rule.
  if (state.isSuperManager()) {
    const inMyOrgs = state.organizations().some(o => o.principal_id === assessment.org_id);
    return inMyOrgs
      ? { allowed: true }
      : { allowed: false, reasonKey: 'export_permissions.external_org' };
  }

  // Managers don't need a subscription — they manage on behalf of a
  // customer that does. The BE enforces this in detail.
  if (state.isManager()) return { allowed: true };

  // Standard user — needs an active subscription on their own org.
  return state.hasActiveSubscription()
    ? { allowed: true }
    : { allowed: false, reasonKey: 'export_permissions.requires_subscription' };
}

export function canExportAssessment(
  state: StateStoreService,
  assessment: AssessmentRecord | null | undefined
): boolean {
  return getAssessmentExportPermission(state, assessment).allowed;
}
